import {
  Button as AntButton,
  Modal,
  Select,
  Table,
  TableColumnsType,
} from 'antd';
import _ from 'lodash';
import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { createEventRegister, getParents } from '../../../../../api/events.api';
import useAxios from '../../../../../hooks/useAxios';
import useNotification from '../../../../../hooks/useNotification';
import config from '../../../../../lib/config';
import { getCommonCodeLabel } from '../../../../../lib/store';
import { IGetParents, IParent } from '../../../../../models/events.model';
import { RootState } from '../../../../../store/rootReducer';
import Button from '../../../../Button';
import Checkbox from '../../../../Checkbox';
import ErrorResponse from '../../../../ErrorResponse';
import Loader from '../../../../Loader';
import Pagination from '../../../../Pagination';
import SelectCampus from '../../../../SelectCampus';

interface IProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  eventNo: number | undefined;
  doGetEventRegister: () => void;
}

const SelectParentModal: FC<IProps> = ({
  visible,
  setVisible,
  eventNo,
  doGetEventRegister,
}) => {
  const { t } = useTranslation();
  const { currentCampus } = useSelector((state: RootState) => state.auth);
  const { infoNotification, successNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetParents>();
  const {
    data: submitRes,
    isLoading: submitIsLoading,
    error: submitError,
    execute: submitExecute,
  } = useAxios();

  const [data, setData] = useState<IParent[]>([]);
  const [page, setPage] = useState(1);
  const [field, setField] = useState('memberName');
  const [keyword, setKeyword] = useState('');
  const [selects, setSelects] = useState<string[]>([]);
  const [isSelectAll, setIsSelectAll] = useState(false);

  useEffect(() => {
    if (eventNo) {
      execute(getParents(eventNo));
    }
  }, [eventNo]);

  useEffect(() => {
    onSearch();
  }, [currentCampus]);

  useEffect(() => {
    if (res && !isLoading) {
      console.log('data', res.data)
      setData(res.data);
    }
  }, [res, isLoading]);

  useEffect(() => {
    if (submitRes && !submitIsLoading) {
      doGetEventRegister();
      successNotification(t('common.message.save1'));
      onClose();
    }
  }, [submitRes, submitIsLoading]);

  // memberCode: 학생의 memberCode (목록 데이터에서는 studentCode)
  const onSetSelects = (memberCode: string) => {
    if (selects.includes(memberCode)) {
      setSelects(selects.filter((item) => item !== memberCode));
      setIsSelectAll(false);
    } else {
      setSelects([...selects, memberCode]);
    }
  };

  const onSelectAll = () => {
    if (isSelectAll) {
      setSelects([]);
      setIsSelectAll(false);
    } else {
      const _selects = _.uniq(data.map((item) => item.studentCode));
      setSelects(_selects);
      setIsSelectAll(true);
    }
  };

  const onSearch = () => {
    let _data = _.cloneDeep(res?.data);

    if (_data) {
      if (currentCampus) {
        _data = _data.filter(
          (item) => item.studentCampusCode === currentCampus,
        );
      }
      if (field && keyword) {
        switch (field) {
          case 'memberName':
            _data = _data.filter(
              (item) =>
                item.memKorName?.includes(keyword) ||
                item.memEngName?.includes(keyword),
            );
            break;
          case 'studentName':
            _data = _data.filter(
              (item) =>
                item.studentKorName?.includes(keyword) ||
                item.studentEngName?.includes(keyword),
            );
            break;
          case 'phone':
            _data = _data.filter((item) => item.mobilePhone?.includes(keyword));
            break;
        }
      }
      setData(_data);
    }
  };

  const onSubmit = () => {
    if (!eventNo) return;
    if (selects.length === 0) {
      return infoNotification(t('event.education.message11'));
    }

    const req: any = [];
    selects.forEach(studentCode => {
      req.push({
        campusCode: data.find((item) => item.studentCode === studentCode)
          ?.studentCampusCode,
        memberCode: studentCode,
      });
    });

    submitExecute(createEventRegister('PA', eventNo, req));
  };

  const onClose = () => {
    setSelects([]);
    setIsSelectAll(false);
    setVisible(false);
  };

  const columns: TableColumnsType<IParent> = [
    {
      title: t('event.briefing.column4'),
      dataIndex: 'memKorName',
      sorter: (a, b) => a.memKorName?.localeCompare(b.memKorName),
      align: 'center',
    },
    {
      title: t('common.menu.campus'),
      dataIndex: 'studentCampusKorName',
      sorter: (a, b) =>
        a.studentCampusKorName?.localeCompare(b.studentCampusKorName),
      align: 'center',
    },
    {
      title: t('event.briefing.column5'),
      dataIndex: 'studentKorName',
      sorter: (a, b) => a.studentKorName?.localeCompare(b.studentKorName),
      align: 'center',
    },
    {
      title: t('event.briefing.column6'),
      dataIndex: 'schoolKorName',
      sorter: (a, b) => a.schoolKorName?.localeCompare(b.schoolKorName),
      align: 'center',
    },
    {
      title: t('event.briefing.column7'),
      dataIndex: 'gradeCode',
      sorter: (a, b) => a.gradeCode?.localeCompare(b.gradeCode),
      align: 'center',
      render: (value) => getCommonCodeLabel('SY0006', value, 'ko'),
    },
    {
      title: t('event.briefing.column8'),
      dataIndex: 'mobilePhone',
      sorter: (a, b) => a.mobilePhone?.localeCompare(b.mobilePhone),
      align: 'center',
      render: value => value ? value.slice(0, -4) + '****' : '',
    },
    {
      title: (
        <Checkbox
          checked={isSelectAll}
          onClickCapture={(e: any) => {
            e.stopPropagation();
            onSelectAll();
          }}
          text=""
        />
      ),
      dataIndex: 'studentCode',
      align: 'center',
      sorter: (a: any, b: any) => {
        let num1 = 0;
        let num2 = 0;
        if (selects.includes(a.studentCode)) num1 = 1;
        if (selects.includes(b.studentCode)) num2 = 1;

        return num1 - num2;
      },
      render: (value) => {
        let checked = false;
        if (selects.includes(value)) checked = true;

        return (
          <div className="text-center">
            <Checkbox
              checked={checked}
              onChange={() => onSetSelects(value)}
              text=""
            />
          </div>
        );
      },
    },
  ];

  return (
    <>
      {isLoading || submitIsLoading ? <Loader /> : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={submitError} />

      <Modal
        title={t('event.briefing.title6')}
        visible={visible}
        onCancel={onClose}
        width={700}
        footer={[
          <div className='comm-btn__area justify-content-center'>	
            <AntButton
              key="submit"
              className='btn-basic pink'
              onClick={onSubmit}
            >
              {t('event.education.button8')}
            </AntButton>
            <AntButton
              key="cancel"
              className='btn-basic gray'
              onClick={onClose}
            >
              {t('common.button.cancel')}
            </AntButton>
          </div>
        ]}
      >
        <div className="flex-justify-end comm-gap">
          <div className="w-150p">
            <SelectCampus />
          </div>
          <div className="w-150p">
            <Select
              className="w-100"
              placeholder={t('common.button.select')}
              value={field}
              onChange={(value) => setField(value)}
            >
              <Select.Option value="memberName">{t('event.briefing.column9')}</Select.Option>
              <Select.Option value="studentName">{t('event.briefing.column5')}</Select.Option>
              <Select.Option value="phone">{t('event.briefing.column8')}</Select.Option>
            </Select>
          </div>
          <div className="flex-fill">
            <input
              type="text"
              className="form-control"
              placeholder={t('common.text.input')}
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') onSearch();
              }}
            />
          </div>
          <div>
            <Button
              className="btn-basic black small"
              onClick={onSearch}
              text={t('common.button.search')}
            />
          </div>
        </div>
        <div>
          <Table
            className="comm-table__area"
            columns={columns}
            dataSource={data}
            pagination={{
              current: page,
              pageSize: config.miniPageSize,
            }}
          />
        </div>
        <div className='comm-bx__between'>
          <div className="common-paging center">
            <Pagination
              count={data?.length || 0}
              page={page}
              setPage={setPage}
              mini
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default SelectParentModal;
